import moment from "moment";

export const PERIODS: [string, string][] = [
  ["all", "All time"],
  ["today", "Today"],
  ["week", "This Week"],
  ["month", "This Month"],
  ["d30", "Last 30 days"],
  ["quarter", "This Quarter"],
  ["custom", "Custom"],
];

// Date range of a period. "all" has no range; "custom" uses the from / to dates (YYYY-MM-DD)
export const periodRange = (period: string, from = "", to = ""): [moment.Moment | null, moment.Moment | null] => {
  switch (period) {
    case "all": return [null, null];
    case "custom": return [from ? moment(from).startOf("day") : null, to ? moment(to).endOf("day") : null];
    case "today": return [moment().startOf("day"), moment().endOf("day")];
    case "month": return [moment().startOf("month"), moment().endOf("month")];
    case "d30": return [moment().subtract(29, "days").startOf("day"), moment().endOf("day")];
    case "quarter": return [moment().startOf("quarter"), moment().endOf("quarter")];
    case "week":
    default: return [moment().startOf("week"), moment().endOf("week")];
  }
};

interface PeriodFilterProps {
  value: string;
  onChange: (period: string) => void;
  // show the "All time" option
  allTime?: boolean;
  // custom range (YYYY-MM-DD); the "Custom" option only shows when onRangeChange is passed
  from?: string;
  to?: string;
  onRangeChange?: (from: string, to: string) => void;
}

// Period dropdown + date range, shown in page headers
const PeriodFilter = ({ value, onChange, allTime, from = "", to = "", onRangeChange }: PeriodFilterProps) => {
  const options = PERIODS.filter(([k]) => (k !== "all" || allTime) && (k !== "custom" || onRangeChange));

  // switching to Custom starts from the range that was showing
  const handleChange = (period: string) => {
    if (period === "custom" && onRangeChange && (!from || !to)) {
      const [f, t] = periodRange(value === "all" || value === "custom" ? "month" : value);
      onRangeChange(f!.format("YYYY-MM-DD"), t!.format("YYYY-MM-DD"));
    }
    onChange(period);
  };

  const [f, t] = periodRange(value, from, to);
  const label = value === "all" || !f || !t
    ? "All dates"
    : value === "today" ? f.format("MMM DD, YYYY") : `${f.format("MMM DD, YYYY")} - ${t.format("MMM DD, YYYY")}`;

  return (
    <div className="cx-pf">
      <select className="cx-select" value={value} onChange={(e) => handleChange(e.target.value)}>
        {options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      {value === "custom" && onRangeChange ? (
        <span className="dr custom">
          <input type="date" value={from} max={to || undefined} onChange={(e) => onRangeChange(e.target.value, to)} aria-label="From date" />
          –
          <input type="date" value={to} min={from || undefined} onChange={(e) => onRangeChange(from, e.target.value)} aria-label="To date" />
        </span>
      ) : (
        <span className="dr"><i className="ri-calendar-2-line"></i>{label}</span>
      )}
    </div>
  );
};

export default PeriodFilter;
